import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// @mui components
import {
  Card,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Custom components
import MDBox from "shared/components/MDBox";
import MDInput from "shared/components/MDInput";
import MDButton from "shared/components/MDButton";
import BasicLayout from "modules/authentication/components/BasicLayout";

// Context y servicios
import { useAuth } from "app/providers/authContext";
import { loginService } from "core/auth/authService";

// Utilidades de seguridad
import { validateInput } from "shared/utils/securityUtils";

// Imagen de fondo
import bgImage from "assets/images/WalPaper-GP.png";
import logoHorizontal from "assets/images/goper2.png";

// Configuración de seguridad
const SECURITY = {
  MAX_ATTEMPTS: 5,
  LOCKOUT_TIME: 15 * 60 * 1000,
  MIN_PASSWORD_LENGTH: 6,
  DEBOUNCE_TIME: 1000,
};

// Componente para preload de imagen
const ImagePreloader = ({ src, onLoad, onError }) => {
  useEffect(() => {
    const img = new Image();
    img.src = src;

    img.onload = () => onLoad?.();
    img.onerror = () => onError?.();

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, onLoad, onError]);

  return null;
};

// Validación de PropTypes para ImagePreloader
ImagePreloader.propTypes = {
  src: PropTypes.string.isRequired,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

ImagePreloader.defaultProps = {
  onLoad: () => {},
  onError: () => {},
};

function Basic() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const timeoutRef = useRef(null);
  const { login } = useAuth();

  // Preload de la imagen al montar el componente
  useEffect(() => {
    const storedAttempts = localStorage.getItem("loginAttempts");
    const storedLockout = localStorage.getItem("lockoutUntil");

    const until = Number(storedLockout);
    if (until > Date.now()) {
      setAttempts(Number(storedAttempts) || 0);
      setLockoutUntil(until);
      setRemainingTime(Math.ceil((until - Date.now()) / 1000));
    } else if (storedLockout) {
      localStorage.removeItem("loginAttempts");
      localStorage.removeItem("lockoutUntil");
    } else {
      setAttempts(Number(storedAttempts) || 0);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Efecto para el contador regresivo durante el bloqueo
  useEffect(() => {
    let interval;
    if (lockoutUntil && lockoutUntil > Date.now()) {
      interval = setInterval(() => {
        const timeLeft = Math.ceil((lockoutUntil - Date.now()) / 1000);
        setRemainingTime(timeLeft);

        if (timeLeft <= 0) {
          setLockoutUntil(null);
          setAttempts(0);
          localStorage.removeItem("lockoutUntil");
          localStorage.removeItem("loginAttempts");
          clearInterval(interval);
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lockoutUntil]);

  // Manejadores de estado de la imagen
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true); // Permitir que continúe incluso si falla
  };

  // Función para manejar el bloqueo por intentos excesivos
  const handleFailedAttempt = () => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    localStorage.setItem("loginAttempts", newAttempts.toString());

    if (newAttempts >= SECURITY.MAX_ATTEMPTS) {
      const lockoutTime = Date.now() + SECURITY.LOCKOUT_TIME;
      setLockoutUntil(lockoutTime);
      localStorage.setItem("lockoutUntil", lockoutTime.toString());

      setError(`Demasiados intentos fallidos. Espere ${SECURITY.LOCKOUT_TIME / 60000} minutos.`);
      setOpenSnackbar(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Verificar si está en periodo de bloqueo
    if (lockoutUntil && lockoutUntil > Date.now()) {
      setError(`Demasiados intentos fallidos. Espere ${Math.ceil(remainingTime / 60)} minutos.`);
      setOpenSnackbar(true);
      return;
    }

    // Validaciones de entrada
    if (!username || !password) {
      setError("Por favor ingresa usuario y contraseña");
      setOpenSnackbar(true);
      return;
    }

    // Validar longitud mínima de contraseña
    if (password.length < SECURITY.MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${SECURITY.MIN_PASSWORD_LENGTH} caracteres`);
      setOpenSnackbar(true);
      return;
    }

    const normalizedUsername = username.trim();

    // Validar formato de entradas
    if (!validateInput(normalizedUsername, "username")) {
      setError("El formato del usuario no es válido");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      // Delay de seguridad con valor aleatorio para dificultar timing attacks
      const randomDelay = 600 + Math.floor(Math.random() * 400);
      await new Promise((resolve) => setTimeout(resolve, randomDelay));

      // El servicio se encarga del cifrado
      const data = await loginService(normalizedUsername, password);

      login(data);
      localStorage.removeItem("loginAttempts");
      localStorage.removeItem("lockoutUntil");
      setAttempts(0);
      setLockoutUntil(null);

      // Limpieza segura de la contraseña
      setPassword("");
    } catch (err) {
      setError(err.message || "No se pudo iniciar sesión. Intenta de nuevo.");
      if (err.invalidCredentials) handleFailedAttempt();
      setOpenSnackbar(true);
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {/* Preload de la imagen en segundo plano */}
      <ImagePreloader src={bgImage} onLoad={handleImageLoad} onError={handleImageError} />

      {/* Loading overlay sutil mientras carga la imagen */}
      {!imageLoaded && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "background.default",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            transition: "opacity 0.3s ease",
          }}
        >
          <CircularProgress color="success" size={40} />
        </Box>
      )}

      <BasicLayout image={imageLoaded ? bgImage : ""}>
        <Card
          sx={{
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s ease",
            transform: imageLoaded ? "translateY(0)" : "translateY(10px)",
          }}
        >
          <MDBox
            variant="gradient"
            borderRadius="lg"
            coloredShadow="info"
            mx={1}
            mt={-3}
            p={2}
            mb={1}
            textAlign="center"
            sx={{
              backgroundColor: "#00E400",
              color: "#ffffff",
              background: "linear-gradient(135deg, #00E400 0%, #00A300 100%)",
            }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="center">
              <img
                src={logoHorizontal}
                alt="Logo Goper"
                style={{
                  height: "45px",
                  objectFit: "contain",
                  marginRight: "1px",
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </MDBox>
          </MDBox>

          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleSubmit}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Usuario"
                  fullWidth
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  inputProps={{
                    maxLength: 50,
                    pattern: "[a-zA-Z0-9@.]+",
                  }}
                  disabled={!imageLoaded || loading}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Contraseña"
                  fullWidth
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{
                    minLength: SECURITY.MIN_PASSWORD_LENGTH,
                    maxLength: 100,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          disabled={!imageLoaded || loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={!imageLoaded || loading}
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  type="submit"
                  disabled={!imageLoaded || loading || (lockoutUntil && lockoutUntil > Date.now())}
                  sx={{
                    backgroundColor: "#00E400",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#66f566",
                      color: "#000000",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                    },
                    transition: "all 0.3s ease",
                    opacity: imageLoaded ? 1 : 0.7,
                  }}
                  fullWidth
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : lockoutUntil && lockoutUntil > Date.now() ? (
                    `Espere ${Math.ceil(remainingTime / 60)} min`
                  ) : !imageLoaded ? (
                    "Cargando..."
                  ) : (
                    "Ingresar"
                  )}
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>

        {/* Mensaje de error de imagen (solo en desarrollo) */}
        {imageError && process.env.NODE_ENV === "development" && (
          <Snackbar
            open={imageError}
            autoHideDuration={3000}
            onClose={() => setImageError(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity="warning" sx={{ width: "100%" }}>
              Error al cargar la imagen de fondo
            </Alert>
          </Snackbar>
        )}
      </BasicLayout>
    </>
  );
}

export default Basic;
